// Shared domain types used by both the Cloudflare Worker (backend) and the
// React admin/kiosk frontend. Keep this file free of runtime dependencies so
// it can be imported from either environment.

export type FitMode = "cover" | "contain" | "fill";
export type Rotation = 0 | 90 | 180 | 270;
export type SlideType =
  | "image"
  | "video"
  | "text"
  | "groupref"
  | "clock"
  | "youtube"
  | "vimeo"
  | "review"
  | "qr"
  | "team";

export type ReviewSource = "google" | "yelp" | "other";

/** Captions: "off" disables, "auto" uses the browser/embedded track, or a
 *  string R2 key pointing at an uploaded .vtt file. */
export type CaptionMode = "off" | "auto" | { vttKey: string };

/** When a slide is allowed to play. All fields optional; an absent field means
 *  "no restriction" for that dimension. Evaluated in the viewer's local time. */
export interface SlideSchedule {
  /** Inclusive YYYY-MM-DD start date. */
  startDate?: string;
  /** Inclusive YYYY-MM-DD end date. */
  endDate?: string;
  /** Allowed weekdays, 0=Sun … 6=Sat. Empty/absent = every day. */
  days?: number[];
  /** Daily window start "HH:MM" (24h). */
  startTime?: string;
  /** Daily window end "HH:MM" (24h). */
  endTime?: string;
}

export interface BaseSlide {
  id: string;
  type: SlideType;
  enabled: boolean;
  /** Seconds to show the slide. Ignored for videos that play to their end. */
  durationSec: number;
  /** Optional group label. Slides sharing a group stay together (in list
   *  order) as a single block, even when shuffle is on. */
  group?: string;
  /** Optional play schedule. Absent = always (when enabled). */
  schedule?: SlideSchedule;
  /** Hide the clinic logo overlay while this slide is showing. */
  hideLogo?: boolean;
}

export interface MediaSlide extends BaseSlide {
  type: "image" | "video";
  /** R2 object key for the uploaded media. */
  mediaKey: string;
  /** Original filename, shown in the admin. */
  fileName?: string;
  fit: FitMode;
  rotation: Rotation;
}

export interface VideoSlide extends MediaSlide {
  type: "video";
  /** Per-video audio override. When false (default) the video is muted. */
  audio: boolean;
  /** Whether the video should loop within its slot. */
  loop: boolean;
  captions: CaptionMode;
}

export interface ImageSlide extends MediaSlide {
  type: "image";
  /** Slow Ken Burns pan & zoom. Off by default. */
  kenBurns?: boolean;
}

export interface TextSlide extends BaseSlide {
  type: "text";
  title: string;
  body: string;
  /** CSS color strings. */
  bgColor: string;
  textColor: string;
  align: "left" | "center" | "right";
}

/** A reference, placed in a workspace playlist, to a reusable Shared Group.
 *  At playback it expands to the linked group's current slides. */
export interface GroupRefSlide {
  id: string;
  type: "groupref";
  enabled: boolean;
  sharedGroupId: string;
  /** Cached name for display in the editor (the group is the source of truth). */
  name?: string;
  /** Optional play schedule applied to the whole group. */
  schedule?: SlideSchedule;
  /** Hide the clinic logo while this group's slides show. */
  hideLogo?: boolean;
}

/** A live clock + optional local weather slide. */
export interface ClockSlide extends BaseSlide {
  type: "clock";
  showDate: boolean;
  showSeconds: boolean;
  hour24: boolean;
  showWeather: boolean;
  /** Free-text location label, also used for geocoding the weather. */
  locationName?: string;
  latitude?: number;
  longitude?: number;
  tempUnit: "F" | "C";
  bgColor: string;
  textColor: string;
}

/** A YouTube video slide. Plays muted + autoplay, filling the screen. */
export interface YouTubeSlide extends BaseSlide {
  type: "youtube";
  videoId: string;
  title?: string;
  /** Loop the video instead of advancing when it ends. */
  loop: boolean;
}

/** A Vimeo video slide. Plays muted + autoplay, filling the screen. */
export interface VimeoSlide extends BaseSlide {
  type: "vimeo";
  /** Numeric Vimeo video id. */
  videoId: string;
  /** Privacy hash for unlisted videos (vimeo.com/<id>/<hash>). */
  hash?: string;
  title?: string;
  loop: boolean;
}

/** A team / provider spotlight card: photo + name + role + short bio. */
export interface TeamSlide extends BaseSlide {
  type: "team";
  /** R2 key for the headshot (optional). */
  photoKey?: string;
  name: string;
  role: string;
  bio: string;
  bgColor: string;
  textColor: string;
}

/** A QR-code slide — a scannable code with a heading and caption. */
export interface QrSlide extends BaseSlide {
  type: "qr";
  /** The URL or text to encode. */
  data: string;
  title?: string;
  caption?: string;
  bgColor: string;
  /** QR foreground (module) color. */
  fgColor: string;
}

/** A patient-review highlight card (Google / Yelp). Content is curated. */
export interface ReviewSlide extends BaseSlide {
  type: "review";
  quote: string;
  author: string;
  /** Whole-star rating, 1–5. */
  rating: number;
  source: ReviewSource;
  bgColor: string;
  textColor: string;
}

export type Slide =
  | ImageSlide
  | VideoSlide
  | TextSlide
  | GroupRefSlide
  | ClockSlide
  | YouTubeSlide
  | VimeoSlide
  | ReviewSlide
  | QrSlide
  | TeamSlide;

/** A slide that can actually be rendered (group references resolved away). */
export type PlayableSlide =
  | ImageSlide
  | VideoSlide
  | TextSlide
  | ClockSlide
  | YouTubeSlide
  | VimeoSlide
  | ReviewSlide
  | QrSlide
  | TeamSlide;

/** A reusable, named collection of slides that can be linked into multiple
 *  workspaces. Editing it updates every workspace that references it. */
export interface SharedGroup {
  id: string;
  name: string;
  ownerId: string;
  slides: Slide[];
  createdAt: number;
}

export interface SharedGroupSummary {
  id: string;
  name: string;
  slideCount: number;
  createdAt: number;
}

/** A persistent clock (and optional weather) shown over the playlist. */
export interface ClockOverlayConfig {
  enabled: boolean;
  showDate: boolean;
  showSeconds: boolean;
  hour24: boolean;
  showWeather: boolean;
  locationName?: string;
  latitude?: number;
  longitude?: number;
  tempUnit: "F" | "C";
}

export type LogoCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right";

/** Clinic branding: a persistent logo overlay and default text-slide colors. */
export interface BrandingConfig {
  /** R2 key of the uploaded logo image. */
  logoKey?: string;
  logoCorner: LogoCorner;
  /** Logo height as a percentage of screen height. */
  logoSizePct: number;
  /** Logo opacity 0–100. */
  logoOpacity: number;
  /** Use the brand colors as defaults for new text slides. */
  useBrandTextColors: boolean;
  brandBgColor: string;
  brandTextColor: string;
}

export interface PlaylistSettings {
  /** Idle seconds before the screensaver takes over the kiosk page. */
  idleTimeoutSec: number;
  /** Crossfade duration between slides, in milliseconds. */
  crossfadeMs: number;
  /** Default duration applied to newly added image/text slides. */
  defaultDurationSec: number;
  /** When true, shuffle slide order on each loop. */
  shuffle: boolean;
  /** Optional bottom-right clock/weather overlay. */
  clockOverlay: ClockOverlayConfig;
  /** Clinic logo + brand colors. */
  branding: BrandingConfig;
}

export interface WorkspaceConfig {
  settings: PlaylistSettings;
  slides: Slide[];
}

export type MemberRole = "owner" | "editor";

export interface Workspace {
  slug: string;
  name: string;
  createdAt: number;
}

export interface Membership {
  userId: string;
  email: string;
  role: MemberRole;
}

export interface User {
  id: string;
  email: string;
  createdAt: number;
}

export interface PublicUser {
  id: string;
  email: string;
}

/** Sanitized config exposed to the (unauthenticated) kiosk page. */
export interface PublicWorkspacePayload {
  workspace: Workspace;
  config: WorkspaceConfig;
}

export const DEFAULT_CLOCK_OVERLAY: ClockOverlayConfig = {
  enabled: false,
  showDate: false,
  showSeconds: false,
  hour24: false,
  showWeather: false,
  tempUnit: "F",
};

export const DEFAULT_BRANDING: BrandingConfig = {
  logoCorner: "top-left",
  logoSizePct: 12,
  logoOpacity: 90,
  useBrandTextColors: false,
  brandBgColor: "#0b1120",
  brandTextColor: "#f8fafc",
};

export const DEFAULT_SETTINGS: PlaylistSettings = {
  idleTimeoutSec: 60,
  crossfadeMs: 1200,
  defaultDurationSec: 8,
  shuffle: false,
  clockOverlay: DEFAULT_CLOCK_OVERLAY,
  branding: DEFAULT_BRANDING,
};

export const DEFAULT_CONFIG: WorkspaceConfig = {
  settings: DEFAULT_SETTINGS,
  slides: [],
};
